// @ts-nocheck
import Editor from './../../../../../components/Editor/Editor';
import { CalendarBlank, Clock, FileText, MapPin } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { formatTimeLocale } from '../../../../../utils/dateUtility';
import { parseISO, format } from 'date-fns';
import { openMaps } from '../../../helpers/contact';
import { useTimeFormat } from '../../../../../hooks/useTimeFormat';
import * as PropTypes from 'prop-types';

/**
 * A service card component for car rentals.
 *
 * @param {Object} servicefields - the fields of the service
 * @param {string} tripdetaileddayserviceid - the id of the tripdetaileddayservice
 * @param {boolean} isPreview - whether the service card is in library preview mode
 * @param {boolean} isDesktop - whether the view is desktop
 * @returns {JSX.Element}
 */
const CarCard = ({
  servicefields,
  tripdetaileddayserviceid,
  isPreview,
  isDesktop
}) => {
  const { t } = useTranslation(['services']);
  const { timeFormat } = useTimeFormat();

  //Preview Content
  if (isPreview) {
    return (
      <div>
        <div className='activity-card__header'>
          <h3 className='activity-card__title'>{servicefields?.TITLE || ''}</h3>
        </div>
        {servicefields?.PICKUPTIME && (
          <div className='activity-card__time'>
            <Clock size={14} weight='regular' />
            <span>
              {formatTimeLocale(servicefields?.PICKUPTIME, timeFormat)}
            </span>
          </div>
        )}

        <div className='activity-card__description-container'>
          {servicefields?.RENTALCOMPANY && (
            <div>
              <p className='activity-card__description'>
                {servicefields?.RENTALCOMPANY || ''}
              </p>
            </div>
          )}

          {servicefields?.CONFIRMATIONINFORMATION && (
            <div className='activity-card__confirmation'>
              {t('services:confirmationLabel', 'Confirmation')}:{' '}
              {servicefields?.CONFIRMATIONINFORMATION || ''}
            </div>
          )}
        </div>

        {isDesktop && (
          <>
            <div className='activity-card__desktop-preview'>
              <div className='activity-card__modal-field'>
                {servicefields?.PICKUPDATE || servicefields?.PICKUPTIME ? (
                  <p className='activity-card__modal-field__title'>
                    {t('services:pickup', 'Pickup')}
                  </p>
                ) : null}

                {servicefields?.PICKUPDATE && (
                  <div className='activity-card__modal-field__item'>
                    <CalendarBlank size={16} color='#667387' />
                    <p className='activity-card__modal-field__value'>
                      <span className='activity-card__modal-field__label'>
                        {t('services:date', 'Date')}:
                      </span>{' '}
                      {format(
                        parseISO(servicefields?.PICKUPDATE?.split('T')[0]),
                        'MMM d, yyyy'
                      )}
                    </p>
                  </div>
                )}

                {servicefields?.PICKUPTIME && (
                  <div className='activity-card__modal-field__item'>
                    <Clock size={16} color='#667387' />
                    <p className='activity-card__modal-field__value'>
                      <span className='activity-card__modal-field__label'>
                        {t('services:time', 'Time')}:
                      </span>{' '}
                      {formatTimeLocale(servicefields?.PICKUPTIME, timeFormat)}
                    </p>
                  </div>
                )}

                {servicefields?.PICKUPADDRESS && (
                  <div className='activity-card__modal-field__item'>
                    <MapPin size={16} color='#667387' />
                    <a
                      className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                      role='button'
                      tabIndex={0}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openMaps(servicefields.PICKUPADDRESS);
                      }}
                      title={t('services:openInMaps', 'Open in Maps')}
                    >
                      <span className='selectable-link'>
                        {servicefields?.PICKUPADDRESS || ''}
                      </span>
                    </a>
                  </div>
                )}
              </div>
              <div className='activity-card__modal-field'>
                {servicefields?.DROPOFFDATE || servicefields?.DROPOFFTIME ? (
                  <p className='activity-card__modal-field__title'>
                    {t('services:dropoff', 'Dropoff')}
                  </p>
                ) : null}

                {servicefields?.DROPOFFDATE && (
                  <div className='activity-card__modal-field__item'>
                    <CalendarBlank size={16} color='#667387' />
                    <p className='activity-card__modal-field__value'>
                      <span className='activity-card__modal-field__label'>
                        {t('services:date', 'Date')}:
                      </span>{' '}
                      {format(
                        parseISO(servicefields?.DROPOFFDATE?.split('T')?.[0]),
                        'MMM d, yyyy'
                      )}
                    </p>
                  </div>
                )}

                {servicefields?.DROPOFFTIME && (
                  <div className='activity-card__modal-field__item'>
                    <Clock size={16} color='#667387' />
                    <p className='activity-card__modal-field__value'>
                      <span className='activity-card__modal-field__label'>
                        {t('services:time', 'Time')}:
                      </span>{' '}
                      {formatTimeLocale(servicefields?.DROPOFFTIME, timeFormat)}
                    </p>
                  </div>
                )}

                {servicefields?.DROPOFFADDRESS && (
                  <div className='activity-card__modal-field__item'>
                    <MapPin size={16} color='#667387' />
                    <a
                      className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                      role='button'
                      tabIndex={0}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openMaps(servicefields.DROPOFFADDRESS);
                      }}
                      title={t('services:openInMaps', 'Open in Maps')}
                    >
                      <span className='selectable-link'>
                        {servicefields?.DROPOFFADDRESS || ''}
                      </span>
                    </a>
                  </div>
                )}
              </div>
              {servicefields?.CARCLASS && (
                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:vehicleClass', 'Vehicle Class')}
                  </p>
                  <div className='activity-card__modal-field__item'>
                    <p className='activity-card__modal-field__value'>
                      {servicefields.CARCLASS || ''}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {servicefields?.DESCRIPTION && (
              <div className='activity-card__modal-rich-text'>
                <Editor
                  id={`service-editor-${tripdetaileddayserviceid}-description`}
                  content={servicefields?.DESCRIPTION}
                  editable={false}
                  isPreviewing={true}
                />
              </div>
            )}
            {servicefields?.UPLOADATTACHMENTS?.length > 0 && (
              <div className='activity-card__modal-field'>
                <p className='activity-card__modal-field__title'>
                  {t('services:attachedDocument', 'Attached Document')}
                </p>
                <div>
                  {servicefields?.UPLOADATTACHMENTS?.map((attachment, idx) => (
                    <a
                      key={idx}
                      href={attachment?.url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='activity-card__attachment-link'
                      title={t('services:visitWebsite', 'Visit Website')}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <FileText size={20} />
                      <span>{attachment?.originalName}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  //Modal Content
  return (
    <div id={`service-card-rental-${tripdetaileddayserviceid}`}>
      <div>
        {servicefields?.CONFIRMATIONINFORMATION && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:confirmationLabel', 'Confirmation')}
            </p>
            <div className='activity-card__modal__confirmation'>
              {servicefields?.CONFIRMATIONINFORMATION || ''}
            </div>
          </div>
        )}

        {servicefields?.RENTALCOMPANY && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:provider', 'Provider')}
            </p>
            <p className='activity-card__modal-field__value'>
              {servicefields?.RENTALCOMPANY || ''}
            </p>
          </div>
        )}

        <div className='activity-card__modal-field'>
          {servicefields?.PICKUPDATE || servicefields?.PICKUPTIME ? (
            <p className='activity-card__modal-field__title'>
              {t('services:pickup', 'Pickup')}
            </p>
          ) : null}

          {servicefields?.PICKUPDATE && (
            <div className='activity-card__modal-field__item'>
              <CalendarBlank size={16} color='#667387' />
              <p className='activity-card__modal-field__value'>
                <span className='activity-card__modal-field__label'>
                  {t('services:date', 'Date')}:
                </span>{' '}
                {format(
                  parseISO(servicefields?.PICKUPDATE?.split('T')[0]),
                  'MMM d, yyyy'
                )}
              </p>
            </div>
          )}

          {servicefields?.PICKUPTIME && (
            <div className='activity-card__modal-field__item'>
              <Clock size={16} color='#667387' />
              <p className='activity-card__modal-field__value'>
                <span className='activity-card__modal-field__label'>
                  {t('services:time', 'Time')}:
                </span>{' '}
                {formatTimeLocale(servicefields?.PICKUPTIME, timeFormat)}
              </p>
            </div>
          )}

          {servicefields?.PICKUPADDRESS && (
            <div className='activity-card__modal-field__item'>
              <MapPin size={16} color='#667387' />

              <a
                className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                role='button'
                tabIndex={0}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openMaps(servicefields.PICKUPADDRESS);
                }}
                title={t('services:openInMaps', 'Open in Maps')}
              >
                <span className='selectable-link'>
                  {servicefields?.PICKUPADDRESS || ''}
                </span>
              </a>
            </div>
          )}
        </div>

        <div className='activity-card__modal-field'>
          {servicefields?.DROPOFFDATE || servicefields?.DROPOFFTIME ? (
            <p className='activity-card__modal-field__title'>
              {t('services:dropoff', 'Dropoff')}
            </p>
          ) : null}

          {servicefields?.DROPOFFDATE && (
            <div className='activity-card__modal-field__item'>
              <CalendarBlank size={16} color='#667387' />
              <p className='activity-card__modal-field__value'>
                <span className='activity-card__modal-field__label'>
                  {t('services:date', 'Date')}:
                </span>{' '}
                {format(
                  parseISO(servicefields?.DROPOFFDATE?.split('T')?.[0]),
                  'MMM d, yyyy'
                )}
              </p>
            </div>
          )}

          {servicefields?.DROPOFFTIME && (
            <div className='activity-card__modal-field__item'>
              <Clock size={16} color='#667387' />
              <p className='activity-card__modal-field__value'>
                <span className='activity-card__modal-field__label'>
                  {t('services:time', 'Time')}:
                </span>{' '}
                {formatTimeLocale(servicefields?.DROPOFFTIME, timeFormat)}
              </p>
            </div>
          )}

          {servicefields?.DROPOFFADDRESS && (
            <div className='activity-card__modal-field__item'>
              <MapPin size={16} color='#667387' />
              <a
                className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                role='button'
                tabIndex={0}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openMaps(servicefields.DROPOFFADDRESS);
                }}
                title={t('services:openInMaps', 'Open in Maps')}
              >
                <span className='selectable-link'>
                  {servicefields?.DROPOFFADDRESS || ''}
                </span>
              </a>
            </div>
          )}
        </div>

        {servicefields?.CARCLASS && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:vehicleClass', 'Vehicle Class')}
            </p>
            <div className='activity-card__modal-field__item'>
              <p className='activity-card__modal-field__value'>
                {servicefields.CARCLASS || ''}
              </p>
            </div>
          </div>
        )}

        {servicefields?.UPLOADATTACHMENTS?.length > 0 && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:attachedDocument', 'Attached Document')}
            </p>
            <div>
              {servicefields?.UPLOADATTACHMENTS?.map((attachment, idx) => (
                <a
                  key={idx}
                  href={attachment?.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='activity-card__attachment-link'
                >
                  <FileText size={20} />
                  <span>{attachment?.originalName}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {servicefields?.DESCRIPTION && (
          <div className='activity-card__modal-rich-text'>
            <Editor
              id={`service-editor-${tripdetaileddayserviceid}-description`}
              content={servicefields?.DESCRIPTION}
              editable={false}
              isPreviewing={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

CarCard.propTypes = {
  servicefields: PropTypes.object,
  servicetype: PropTypes.object,
  tripdetaileddayserviceid: PropTypes.number,
  isPreview: PropTypes.bool,
  isDesktop: PropTypes.bool
};

export default CarCard;
